import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  useCreatePartnerMutation,
  useUpdatePartnerMutation,
} from "../api/partnersApi";
import { Partner } from "../types/partner";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  partner?: Partner | null;
}

const AddEditPartner: React.FC<Props> = ({
  open,
  setModalOpen,
  onSuccess,
  partner,
}) => {
  const [formData, setFormData] = useState<{
    company_name: string;
    logo: File | null;
    existingLogo?: string | null;
  }>({
    company_name: "",
    logo: null,
    existingLogo: null,
  });

  const [createPartner] = useCreatePartnerMutation();
  const [updatePartner] = useUpdatePartnerMutation();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (partner) {
      setFormData({
        company_name: partner.company_name,
        logo: null,
        existingLogo: partner.logo,
      });
    } else {
      setFormData({
        company_name: "",
        logo: null,
        existingLogo: null,
      });
    }
  }, [partner, open]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, logo: file }));
  };

  const removeLogo = () => {
    setFormData((prev) => ({
      ...prev,
      logo: null,
      existingLogo: null,
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const fd = new FormData();
    fd.append("company_name", formData.company_name);
    if (formData.logo) {
      fd.append("logo", formData.logo);
    }

    try {
      if (partner?.id) {
        await updatePartner({ id: partner.id, body: fd }).unwrap();
      } else {
        await createPartner({ body: fd }).unwrap();
      }
      setModalOpen(false);
      onSuccess?.();
      setError(null);
    } catch (err) {
      console.error("Error saving partner:", err);
      setError("Failed to save partner. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{partner ? "Edit Partner" : "Add Partner"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          label="Company Name"
          value={formData.company_name}
          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
        />

        <Box mt={2}>
          <Button variant="outlined" component="label">
            Upload Logo
            <input hidden accept="image/*" type="file" onChange={handleLogoChange} />
          </Button>

          {(formData.logo || formData.existingLogo) && (
            <Box mt={2} display="flex" alignItems="center" gap={1}>
              <img
                src={
                  formData.logo
                    ? URL.createObjectURL(formData.logo)
                    : (formData.existingLogo as string)
                }
                alt="Logo"
                width={100}
              />
              <IconButton onClick={removeLogo} aria-label="remove logo">
                <DeleteIcon />
              </IconButton>
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setModalOpen(false)} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditPartner;
